import { useMemo } from 'react';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import RouterLink from 'src/components/ui/RouterLink';
import FilterPlateCDefault from 'src/components/ui/FilterPlate/connectors/FilterPlateCDefault';
import { VALUE_TYPE } from 'src/components/ui/FilterPlate/constants';
import { CELL_RENDER_TYPE } from 'src/components/ui/CellRender/constants';

import DefaultTable from '..';
import { COLUIMN_MIN_WIDTH } from '../constants';
import { calcDuration } from 'src/utils/helpers/calcDuration';
import {
	GORKO_STATUS_FILTER_OPTIONS,
	getGorkoStatusFilterLabel,
	mapCompanyGorkoStatus,
} from 'src/utils/mappers/companyGorkoStatus.mapper';
import { useFilters } from 'src/hooks/filters/useFilters';
import {
	indexColumnsByCode,
	mapSelectedFilters,
} from 'src/hooks/filters/mapSelectedFilters';

const defultLinkGenerator = ({ id }) => {
	const companyId = id.company.id;

	return `/company/${companyId}`;
};

const DefaultTableCObjections = (props) => {
	const {
		generateLink = defultLinkGenerator,
		objectionTypes = [],
		...rest
	} = props;
	const { getAllFilters } = useFilters();

	const typeEnums = useMemo(
		() =>
			objectionTypes.map((type) => ({
				value: type.id,
				label: type.name,
			})),
		[objectionTypes],
	);

	const columns = useMemo(() => {
		return [
			{
				id: 'gorkoID',
				accessorFn: (data) => data.company?.gorkoId,
				emptyValue: '—',
				header: 'Gorko ID',
				renderType: CELL_RENDER_TYPE.id,
				filter: {
					code: 'companyGorkoId',
					valueType: VALUE_TYPE.number,
				},
			},
			{
				id: 'gorkoStatus',
				header: 'Статус',
				accessorFn: (data) => mapCompanyGorkoStatus(data.company),
				emptyValue: '—',
				minSize: COLUIMN_MIN_WIDTH.status,
				filter: {
					code: 'gorkoStatus',
					valueType: VALUE_TYPE.enum,
					payload: {
						enums: GORKO_STATUS_FILTER_OPTIONS,
					},
				},
			},
			{
				id: 'name',
				header: 'Название',
				accessorKey: 'company.name',
				minSize: COLUIMN_MIN_WIDTH.company,
				filter: {
					code: 'companyName',
					valueType: VALUE_TYPE.string,
				},
				cell: (props) => {
					const val = props.getValue();
					const link = generateLink({ id: props.row.original });
					return (
						<Link to={link} component={RouterLink}>
							{val}
						</Link>
					);
				},
			},
			{
				id: 'createdAt',
				header: 'Дата и время',
				accessorKey: 'createdAt',
				renderType: CELL_RENDER_TYPE.date,
				filter: {
					code: 'createdAt',
					valueType: VALUE_TYPE.date,
				},
			},
			{
				id: 'type',
				header: 'Тип',
				accessorKey: 'type.name',
				emptyValue: '—',
				minSize: 160,
				filter: {
					code: 'type',
					valueType: VALUE_TYPE.enum,
					payload: {
						enums: typeEnums,
					},
				},
			},
			{
				id: 'comment',
				header: 'Комментарий',
				accessorKey: 'comment',
				minSize: 300,
				filter: {
					code: 'comment',
					valueType: VALUE_TYPE.string,
				},
				cell: (props) => {
					const val = props.getValue();
					return (
						<Typography component="span" color="secondary">
							{val}
						</Typography>
					);
				},
			},
			{
				id: 'manager',
				header: 'Менеджер',
				accessorKey: 'createdBy.name',
				minSize: COLUIMN_MIN_WIDTH.manager,
				filter: {
					code: 'createdByName',
					valueType: VALUE_TYPE.string,
				},
			},
			{
				id: 'duration',
				header: 'Не снято',
				accessorFn: (data) => calcDuration(data, 'createdAt'),
				renderType: CELL_RENDER_TYPE.duration,
				filter: {
					code: 'createdAt',
					valueType: VALUE_TYPE.date,
				},
			},
		];
	}, [generateLink, typeEnums]);

	const columnsByCode = useMemo(() => indexColumnsByCode(columns), [columns]);

	const typesById = useMemo(() => {
		const m = new Map();

		for (const type of objectionTypes) {
			m.set(type.id, type);
			m.set(String(type.id), type);
		}

		return m;
	}, [objectionTypes]);

	const selectedFilters = useMemo(() => {
		return mapSelectedFilters(getAllFilters(), columnsByCode, (code) => {
			if (code === 'gorkoStatus') {
				return getGorkoStatusFilterLabel;
			}

			if (code === 'type') {
				return (value) =>
					typesById.get(value)?.name ??
					typesById.get(Number(value))?.name ??
					String(value);
			}

			return null;
		});
	}, [columnsByCode, typesById, getAllFilters]);

	return (
		<DefaultTable
			columns={columns}
			selectedFilters={selectedFilters}
			FilterPlateRP={FilterPlateCDefault}
			{...rest}
		/>
	);
};

DefaultTableCObjections.propTypes = {
	...DefaultTable.propTypes,
};

export default DefaultTableCObjections;
